import { MAX_SIGNATURE_DATA_URL_LENGTH } from '../constants/signing';

/**
 * Shared canvas helpers for signature capture and processing.
 * Used by the draw pad (stroke export), the type tab (rasterization),
 * and the upload tab (background removal + normalization).
 */

/** Alpha values at or below this are treated as fully transparent when scanning ink. */
const INK_ALPHA_THRESHOLD = 8;

/** Pixels this bright that touch a corner-connected region are flood-filled away. */
const FLOOD_LUMINANCE = 200;

/** Luminance keying band: fully opaque at/below LO, fully transparent at/above HI. */
const KEY_LUMINANCE_LO = 180;
const KEY_LUMINANCE_HI = 242;

/** Uploaded images larger than this on either side are downscaled before processing. */
const MAX_UPLOAD_DIMENSION = 1600;

/** Margin (in source pixels) kept around the ink bounding box when trimming uploads. */
const UPLOAD_TRIM_MARGIN = 10;

/** Typed signatures rasterize at this supersample over the 560x180 base canvas. */
export const TYPED_SIGNATURE_SUPERSAMPLE = 3;

function luminanceAt(data: Uint8ClampedArray, byteIndex: number): number {
  return 0.299 * data[byteIndex] + 0.587 * data[byteIndex + 1] + 0.114 * data[byteIndex + 2];
}

/**
 * Return the bounding box of non-transparent pixels, or null when the canvas is blank.
 * Exported so the draw-pad can validate ink before saving (H-1).
 */
export function getInkBoundingBox(
  source: HTMLCanvasElement
): { x: number; y: number; width: number; height: number } | null {
  const ctx = source.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  const { width, height } = source;
  if (!width || !height) return null;
  const data = ctx.getImageData(0, 0, width, height).data;
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y++) {
    const rowOffset = y * width;
    for (let x = 0; x < width; x++) {
      if (data[(rowOffset + x) * 4 + 3] > INK_ALPHA_THRESHOLD) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
}

/**
 * Return true when the canvas contains at least one pixel that reads as ink:
 * opaque enough to be visible AND dark enough not to be (near-)white paper.
 *
 * getInkBoundingBox only looks at alpha, so a fully-opaque all-white image (e.g. a
 * white JPEG uploaded without background removal) reports a full-canvas box and is
 * wrongly treated as having content. This luminance-aware check catches that case,
 * regardless of whether background removal ran.
 */
export function hasVisibleInk(source: HTMLCanvasElement): boolean {
  const ctx = source.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;
  const { width, height } = source;
  if (!width || !height) return false;
  const data = ctx.getImageData(0, 0, width, height).data;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] <= INK_ALPHA_THRESHOLD) continue;
    if (luminanceAt(data, i) < KEY_LUMINANCE_HI) return true;
  }
  return false;
}

/**
 * Crop a canvas to the bounding box of its non-transparent pixels plus a margin.
 * Returns the source canvas unchanged when it is empty or fully transparent.
 */
export function trimCanvasToInk(source: HTMLCanvasElement, marginPx = 12): HTMLCanvasElement {
  const ctx = source.getContext('2d', { willReadFrequently: true });
  if (!ctx) return source;
  const { width, height } = source;
  if (!width || !height) return source;

  const data = ctx.getImageData(0, 0, width, height).data;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y++) {
    const rowOffset = y * width;
    for (let x = 0; x < width; x++) {
      if (data[(rowOffset + x) * 4 + 3] > INK_ALPHA_THRESHOLD) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return source;

  const left = Math.max(0, minX - marginPx);
  const top = Math.max(0, minY - marginPx);
  const right = Math.min(width - 1, maxX + marginPx);
  const bottom = Math.min(height - 1, maxY + marginPx);
  const cropWidth = right - left + 1;
  const cropHeight = bottom - top + 1;

  const out = document.createElement('canvas');
  out.width = cropWidth;
  out.height = cropHeight;
  const outCtx = out.getContext('2d');
  if (!outCtx) return source;
  outCtx.drawImage(source, left, top, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
  return out;
}

/**
 * Make a white/near-white background transparent, in place.
 * Pass 1 flood-fills bright pixels connected to the canvas corners (paper background).
 * Pass 2 keys remaining near-white luminance to alpha so enclosed counters
 * (the inside of 'o', 'a', loops) clear too, with a soft ramp at ink edges.
 */
export function removeWhiteBackground(canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return;
  const { width, height } = canvas;
  if (!width || !height) return;

  const image = ctx.getImageData(0, 0, width, height);
  const data = image.data;
  const pixelCount = width * height;

  const visited = new Uint8Array(pixelCount);
  const stack: number[] = [];
  const isBackground = (pixel: number): boolean => {
    const i = pixel * 4;
    return data[i + 3] <= INK_ALPHA_THRESHOLD || luminanceAt(data, i) >= FLOOD_LUMINANCE;
  };
  const seed = (x: number, y: number) => {
    const pixel = y * width + x;
    if (!visited[pixel] && isBackground(pixel)) {
      visited[pixel] = 1;
      stack.push(pixel);
    }
  };
  seed(0, 0);
  seed(width - 1, 0);
  seed(0, height - 1);
  seed(width - 1, height - 1);

  const tryVisit = (pixel: number) => {
    if (!visited[pixel] && isBackground(pixel)) {
      visited[pixel] = 1;
      stack.push(pixel);
    }
  };
  while (stack.length) {
    const pixel = stack.pop()!;
    data[pixel * 4 + 3] = 0;
    const x = pixel % width;
    const y = (pixel - x) / width;
    if (x > 0) tryVisit(pixel - 1);
    if (x < width - 1) tryVisit(pixel + 1);
    if (y > 0) tryVisit(pixel - width);
    if (y < height - 1) tryVisit(pixel + width);
  }

  for (let pixel = 0; pixel < pixelCount; pixel++) {
    const i = pixel * 4;
    const alpha = data[i + 3];
    if (!alpha) continue;
    const lum = luminanceAt(data, i);
    if (lum <= KEY_LUMINANCE_LO) continue;
    const factor =
      lum >= KEY_LUMINANCE_HI ? 0 : (KEY_LUMINANCE_HI - lum) / (KEY_LUMINANCE_HI - KEY_LUMINANCE_LO);
    data[i + 3] = Math.round(alpha * factor);
  }

  ctx.putImageData(image, 0, 0);
}

/**
 * Export a canvas as a PNG data URL, downscaling progressively until the result
 * fits within the signature data-URL budget.
 * M-7: loop without a fixed iteration cap; switch to JPEG for large photos that
 * stay over-budget as PNG even after aggressive downscaling.
 */
export function canvasToPngDataUrl(canvas: HTMLCanvasElement): string {
  let current = canvas;
  let dataUrl = current.toDataURL('image/png');

  // First try PNG, shrinking 30 % per pass until under budget or too small to continue
  while (dataUrl.length > MAX_SIGNATURE_DATA_URL_LENGTH && current.width > 80) {
    const scaled = document.createElement('canvas');
    scaled.width = Math.max(1, Math.round(current.width * 0.7));
    scaled.height = Math.max(1, Math.round(current.height * 0.7));
    const ctx = scaled.getContext('2d');
    if (!ctx) break;
    ctx.drawImage(current, 0, 0, scaled.width, scaled.height);
    current = scaled;
    dataUrl = current.toDataURL('image/png');
  }

  // If still over-budget (e.g. a photo with many unique colors), fall back to JPEG
  if (dataUrl.length > MAX_SIGNATURE_DATA_URL_LENGTH) {
    let jpegQuality = 0.85;
    dataUrl = current.toDataURL('image/jpeg', jpegQuality);
    while (dataUrl.length > MAX_SIGNATURE_DATA_URL_LENGTH && jpegQuality > 0.2) {
      jpegQuality = Math.max(0.2, jpegQuality - 0.15);
      dataUrl = current.toDataURL('image/jpeg', jpegQuality);
    }
  }

  return dataUrl;
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not decode signature image.'));
    img.src = dataUrl;
  });
}

export interface UploadProcessingOptions {
  removeBackground: boolean;
}

/**
 * Normalize an uploaded signature image: decode, downscale oversized photos,
 * optionally remove the white background, trim to the ink bounding box,
 * and always re-export as PNG (preserving alpha end-to-end).
 */
export async function processUploadedSignature(
  sourceDataUrl: string,
  options: UploadProcessingOptions
): Promise<string> {
  const img = await loadImage(sourceDataUrl);
  const naturalWidth = img.naturalWidth || img.width;
  const naturalHeight = img.naturalHeight || img.height;
  const scale = Math.min(1, MAX_UPLOAD_DIMENSION / Math.max(naturalWidth, naturalHeight, 1));

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(naturalHeight * scale));
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas rendering is not available in this browser.');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  if (options.removeBackground) {
    removeWhiteBackground(canvas);
  }
  // FX-C6 Fix 3 + F24: detect a blank/all-white image. With background removal,
  // a blank image becomes fully transparent (getInkBoundingBox is null). WITHOUT
  // background removal, a white JPEG stays fully opaque, so an alpha-only check
  // would pass and the user would place an invisible (white-on-white) mark. Use a
  // luminance-aware check so the error surfaces regardless of the toggle.
  if (!hasVisibleInk(canvas)) {
    throw new Error('No visible signature content found. Please use an image with a visible signature on a white background.');
  }
  const trimmed = trimCanvasToInk(canvas, UPLOAD_TRIM_MARGIN);
  return canvasToPngDataUrl(trimmed);
}

/**
 * Rasterize a typed signature with a bundled handwriting font.
 * Waits for the webfont, shrinks long names to fit instead of clipping,
 * renders at a supersample for crisp PDF embedding, and trims to ink bounds.
 */
export async function rasterizeTypedSignature(
  text: string,
  fontFamily: string,
  color = '#0f172a'
): Promise<string> {
  const baseWidth = 560;
  const baseHeight = 180;
  const horizontalPad = 24;
  let fontSize = 64;
  const fontFor = (px: number) => `${px}px "${fontFamily}", cursive`;

  try {
    await document.fonts.load(fontFor(fontSize), text);
  } catch {
    // Offline or font blocked: fall back to whatever cursive font is available.
  }

  const ss = TYPED_SIGNATURE_SUPERSAMPLE;
  const canvas = document.createElement('canvas');
  canvas.width = baseWidth * ss;
  canvas.height = baseHeight * ss;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas rendering is not available in this browser.');

  ctx.font = fontFor(fontSize);
  const metrics = ctx.measureText(text);
  const inkWidth = Math.max(
    metrics.width,
    (metrics.actualBoundingBoxLeft || 0) + (metrics.actualBoundingBoxRight || 0)
  );
  const maxWidth = baseWidth - horizontalPad * 2;
  if (inkWidth > maxWidth) {
    fontSize = Math.max(18, Math.floor((fontSize * maxWidth) / inkWidth));
  }

  ctx.setTransform(ss, 0, 0, ss, 0, 0);
  ctx.font = fontFor(fontSize);
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, baseWidth / 2, baseHeight / 2);

  const trimmed = trimCanvasToInk(canvas, 8 * ss);
  return canvasToPngDataUrl(trimmed);
}
